import React, { useMemo } from "react";
import { motion } from "framer-motion";
import {
    PieChart, Pie, Cell, ResponsiveContainer,
    AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const INK = "#0A0A0A";
const COBALT = "#0B3B60";
const GREEN = "#0B6B3A";
const GREY = "#B7B7B0";
const AMBER = "#B5651D";

const Card = ({ children, testId, className = "" }) => (
    <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className={`rounded-2xl border border-oth-line/70 bg-white px-5 py-4 shadow-[0_1px_0_rgba(10,10,10,0.02),0_8px_24px_-16px_rgba(10,10,10,0.18)] ${className}`}
        data-testid={testId}
    >{children}</motion.div>
);

const CardTitle = ({ children }) => (
    <div className="text-[11px] uppercase tracking-[0.18em] text-oth-ink3 font-body mb-3">{children}</div>
);

// ── Pipeline donut ───────────────────────────────────────────────────────────
const PipelineDonut = ({ stats }) => {
    const inPipeline = stats?.in_pipeline ?? 0;
    const converted = stats?.converted ?? 0;
    const closed = stats?.closed ?? 0;
    const data = [
        { name: "In pipeline", value: inPipeline, color: COBALT },
        { name: "Converted", value: converted, color: GREEN },
        { name: "Closed", value: closed, color: GREY },
    ];
    const total = inPipeline + converted + closed;

    return (
        <Card testId="chart-pipeline">
            <CardTitle>Pipeline</CardTitle>
            <div className="flex items-center gap-4">
                <div className="relative w-[120px] h-[120px] shrink-0">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie data={total ? data : [{ name: "none", value: 1, color: "#ECEBE7" }]} dataKey="value"
                                innerRadius={42} outerRadius={58} startAngle={90} endAngle={-270} stroke="none" paddingAngle={total ? 2 : 0}>
                                {(total ? data : [{ color: "#ECEBE7" }]).map((d, i) => <Cell key={i} fill={d.color} />)}
                            </Pie>
                        </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                        <div className="font-display text-2xl font-semibold tracking-tight" style={{ color: INK }}>{total}</div>
                        <div className="text-[9px] uppercase tracking-[0.16em] text-oth-ink3">leads</div>
                    </div>
                </div>
                <div className="space-y-2 flex-1 min-w-0">
                    {data.map((d) => (
                        <div key={d.name} className="flex items-center justify-between" data-testid={`legend-${d.name.replace(/\s+/g, "-").toLowerCase()}`}>
                            <span className="flex items-center gap-2 min-w-0">
                                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: d.color }} />
                                <span className="font-body text-[13px] text-oth-ink2 truncate">{d.name}</span>
                            </span>
                            <span className="font-display text-sm font-semibold" style={{ color: d.color }}>{d.value}</span>
                        </div>
                    ))}
                </div>
            </div>
        </Card>
    );
};

// ── Conversion rate + key numbers ───────────────────────────────────────────
const ConversionPanel = ({ stats }) => {
    const total = stats?.total ?? 0;
    const converted = stats?.converted ?? 0;
    const rate = total ? Math.round((converted / total) * 100) : 0;
    const mini = [
        { label: "Total", value: total, color: INK },
        { label: "Pipeline", value: stats?.in_pipeline ?? 0, color: COBALT },
        { label: "Clients", value: stats?.clients ?? 0, color: GREEN },
        { label: "Closed", value: stats?.closed ?? 0, color: GREY },
    ];
    return (
        <Card testId="chart-conversion">
            <CardTitle>Conversion rate</CardTitle>
            <div className="flex items-end gap-2">
                <div className="font-display text-5xl font-semibold tracking-tight leading-none" style={{ color: GREEN }} data-testid="conversion-rate">{rate}<span className="text-2xl">%</span></div>
                <div className="font-body text-[12px] text-oth-ink3 mb-1.5">{converted} of {total} leads</div>
            </div>
            <div className="mt-3 h-2 rounded-full bg-[#ECEBE7] overflow-hidden">
                <motion.div initial={{ width: 0 }} animate={{ width: `${rate}%` }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="h-full rounded-full" style={{ background: GREEN }} />
            </div>
            <div className="grid grid-cols-4 gap-2 mt-4">
                {mini.map((m) => (
                    <div key={m.label} className="rounded-xl bg-[#FAFAF8] border border-oth-line/50 px-2.5 py-2 text-center" data-testid={`mini-${m.label.toLowerCase()}`}>
                        <div className="font-display text-lg font-semibold leading-none" style={{ color: m.color }}>{m.value}</div>
                        <div className="text-[9px] uppercase tracking-[0.12em] text-oth-ink3 mt-1">{m.label}</div>
                    </div>
                ))}
            </div>
        </Card>
    );
};

// ── Leads over time (last 8 weeks) ──────────────────────────────────────────
const LeadsOverTime = ({ leads }) => {
    const data = useMemo(() => {
        const weeks = 8;
        const now = new Date();
        const buckets = [];
        for (let i = weeks - 1; i >= 0; i--) {
            const end = new Date(now); end.setDate(now.getDate() - i * 7);
            const start = new Date(end); start.setDate(end.getDate() - 6);
            buckets.push({ label: `${start.getMonth() + 1}/${start.getDate()}`, start: start.setHours(0, 0, 0, 0), end: end.setHours(23, 59, 59, 999), count: 0 });
        }
        (leads || []).forEach((l) => {
            const t = Date.parse(l.created_at);
            if (Number.isNaN(t)) return;
            const b = buckets.find((x) => t >= x.start && t <= x.end);
            if (b) b.count += 1;
        });
        return buckets.map((b) => ({ label: b.label, count: b.count }));
    }, [leads]);

    const peak = Math.max(1, ...data.map((d) => d.count));

    return (
        <Card testId="chart-trend">
            <CardTitle>Leads over time · last 8 weeks</CardTitle>
            <div className="h-[148px] -ml-2">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data} margin={{ top: 6, right: 6, left: -18, bottom: 0 }}>
                        <defs>
                            <linearGradient id="leadFill" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={COBALT} stopOpacity={0.28} />
                                <stop offset="100%" stopColor={COBALT} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#ECEBE7" vertical={false} />
                        <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#9A9A9A" }} axisLine={false} tickLine={false} />
                        <YAxis allowDecimals={false} domain={[0, peak]} tick={{ fontSize: 10, fill: "#9A9A9A" }} axisLine={false} tickLine={false} width={28} />
                        <Tooltip cursor={{ stroke: COBALT, strokeOpacity: 0.2 }} contentStyle={{ borderRadius: 12, border: "1px solid #ECEBE7", fontSize: 12, fontFamily: "inherit" }} labelStyle={{ color: INK }} />
                        <Area type="monotone" dataKey="count" stroke={COBALT} strokeWidth={2} fill="url(#leadFill)" dot={{ r: 2.5, fill: COBALT }} activeDot={{ r: 4 }} />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </Card>
    );
};

export const CrmAnalytics = ({ leads, stats }) => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-6" data-testid="crm-analytics">
        <PipelineDonut stats={stats} />
        <ConversionPanel stats={stats} />
        <LeadsOverTime leads={leads} />
    </div>
);

export { AMBER };
